import { useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { TranslateFunction } from 'react-localize-redux';

import { analyticsService } from '../../../../../core/services';
import { QuantityControlType } from '../../../../../types/basket';
import { MenuItem } from '../../../../../weborder/Api';

interface UseCrossSellList {
  crossSellList: MenuItem[];
  onHandleAddItem: (menuItem: MenuItem) => void;
  showCrossSellList: boolean;
}

export const useCrossSellList = (
  addItemToBasket: QuantityControlType['onAddItem'],
  crossSellMenuItems: MenuItem[],
  translate: TranslateFunction,
): UseCrossSellList => {
  const addedCrossSellItemIds = useRef<MenuItem['MenuId'][]>([]);
  const [crossSellList, setCrossSellList] = useState<MenuItem[]>(() => {
    const initialState = crossSellMenuItems.slice(0, 3);
    return initialState;
  });
  const [showCrossSellList, setShowCrossSellList] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (crossSellMenuItems.length > 0) {
      setShowCrossSellList(true);
      setCrossSellList(crossSellMenuItems.slice(0, 3));
    }
  }, [crossSellMenuItems.length]);

  const trackCrossSellItemAdded = (menuItem: MenuItem): void => {
    const itemIndex = crossSellMenuItems.findIndex(
      (item) => item.MenuItemId === menuItem.MenuItemId,
    );
    const eventData = {
      amp_label: 'Cross-sell added',
      amp_indexOfItem: itemIndex,
      amp_itemPrice: menuItem.ActualPrice,
      amp_menuItemId: menuItem.MenuItemId,
      amp_hasImage: !!menuItem.ImageUrl,
    };

    analyticsService.track(eventData);
  };

  const getFilteredCrossSellMenuItems = (menuItem: MenuItem): MenuItem[] => {
    const newAddedCrossSellItemIds = [
      ...addedCrossSellItemIds.current,
      menuItem.MenuItemId,
    ];
    addedCrossSellItemIds.current = newAddedCrossSellItemIds;
    const filteredCrossSellMenuItems = crossSellMenuItems.filter(
      (item) => !newAddedCrossSellItemIds.includes(item.MenuItemId),
    );

    return filteredCrossSellMenuItems;
  };

  const onHandleAddItem = (menuItem: MenuItem): void => {
    trackCrossSellItemAdded(menuItem);
    addItemToBasket({
      Count: 1,
      Item: menuItem,
      Options: [],
    });

    const filteredCrossSellMenuItems = getFilteredCrossSellMenuItems(menuItem);
    setCrossSellList(filteredCrossSellMenuItems.slice(0, 3));

    if (filteredCrossSellMenuItems.length === 0) {
      setShowCrossSellList(false);
    }

    enqueueSnackbar(translate('Basket_itemAdded'), {
      variant: 'success',
    });
  };

  return {
    crossSellList,
    onHandleAddItem,
    showCrossSellList,
  };
};
